import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, where, serverTimestamp, DocumentData } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { userService } from './user.service';
import { subscriptionService } from '../subscription/subscription.service';
import { genericTasksService } from './generic-tasks.service';
import { tasksService } from './tasks.service';

// Catégories de budget par défaut
const DEFAULT_BUDGET_CATEGORIES = {
  venue: 25,
  catering: 25,
  photography: 10,
  music: 8,
  florist: 8,
  dress: 5,
  decoration: 5,
  other: 14
};

// Interface pour un événement
export interface Event {
  id: string;
  name: string;
  date?: string | null;
  type: string;
  location?: string;
  userId: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  isDefault?: boolean;
  // Références aux enregistrements spécifiques dans d'autres collections
  weddingId?: string;
  engagementId?: string;
  birthdayId?: string;
  babyShowerId?: string;
  baptismId?: string;
  otherId?: string;
  partners?: {
    partner1: { firstName: string; lastName?: string; email?: string };
    partner2: { firstName: string; lastName?: string; email?: string };
  };
}

class EventsService {
  private readonly collectionName = 'events';

  /**
  /**
   * Retourne le nom de la collection pour un type d'événement donné
   */
  private getCollectionNameForType(type: string): string | null {
    switch (type.toLowerCase()) {
      case 'wedding': return 'weddings';
      case 'birthday': return 'birthdays';
      case 'engagement': return 'engagements';
      case 'babyshower': return 'babyshowers';
      case 'baptism': return 'baptisms';
      case 'other': return 'other_events';
      default: return null;
    }
  }

  /**
   * Crée un nouvel événement
   */
  async createEvent(userId: string, eventData: Omit<Event, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<Event | null> {
    try {
      // Vérifier les limites d'abonnement
      const currentEvents = await this.getUserEvents(userId);
      const subscription = await subscriptionService.getCurrentSubscription(userId);

      if (!subscription) {
        throw new Error('Impossible de récupérer l\'abonnement de l\'utilisateur');
      }

      // Vérifier si l'utilisateur a atteint sa limite d'événements
      if (currentEvents.length >= subscription.maxEvents) {
        throw new Error(`Vous avez atteint votre limite de ${subscription.maxEvents} événements. Passez à une formule supérieure pour en créer davantage.`);
      }

      // Vérifier si l'utilisateur peut gérer plusieurs événements
      // Cette vérification ne s'applique que si l'utilisateur a déjà au moins 1 événement
      // et que son plan ne permet pas les événements multiples
      if (currentEvents.length >= 1 && !subscription.features.multipleEvents) {
        throw new Error('Votre abonnement ne permet pas de gérer plusieurs événements. Passez à la formule Pro pour cette fonctionnalité.');
      }

      // Créer l'ID du document
      const eventRef = doc(collection(db, this.collectionName));
      const newEventId = eventRef.id;

      // Traiter les données pour s'assurer qu'elles sont compatibles avec Firestore
      // Supprimer les champs undefined qui ne sont pas acceptés par Firestore
      const sanitizedEventData = { ...eventData };

      // Si la date est undefined, la définir comme null
      if (sanitizedEventData.date === undefined) {
        sanitizedEventData.date = null;
      }

      // Construire l'objet event
      const eventToSave: Event = {
        id: newEventId,
        userId,
        createdAt: new Date(),
        updatedAt: new Date(),
        ...sanitizedEventData
      };

      // Sauvegarder l'événement dans Firestore
      await setDoc(eventRef, {
        ...eventToSave,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return eventToSave;
    } catch (error) {
      console.error('Erreur lors de la création de l\'événement :', error);
      throw error;
    }
  }

  /**
   * Récupère tous les événements d'un utilisateur
   */
  async getUserEvents(userId: string): Promise<Event[]> {
    try {
      // Les utilisateurs anonymes n'ont pas d'événements
      if (userId && (await userService.getCurrentUser())?.uid === userId && (await import('firebase/auth')).getAuth().currentUser?.isAnonymous) {
        return [];
      }

      const eventsQuery = query(
        collection(db, this.collectionName),
        where('userId', '==', userId)
      );

      const snapshot = await getDocs(eventsQuery);

      if (snapshot.empty) {
        return [];
      }

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt?.toDate() || null,
          updatedAt: data.updatedAt?.toDate() || null
        } as Event;
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des événements :', error);
      return [];
    }
  }

  /**
   * Récupère un événement par son ID
   */
  async getEventById(eventId: string): Promise<Event | null> {
    try {
      const eventRef = doc(db, this.collectionName, eventId);
      const snapshot = await getDoc(eventRef);

      if (!snapshot.exists()) {
        return null;
      }

      const data = snapshot.data();
      return {
        ...data,
        id: snapshot.id,
        createdAt: data.createdAt?.toDate() || null,
        updatedAt: data.updatedAt?.toDate() || null
      } as Event;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'événement ${eventId} :`, error);
      return null;
    }
  }

  /**
   * Met à jour un événement
   */
  async updateEvent(eventId: string, updates: Partial<Event>): Promise<boolean> {
    try {
      const eventRef = doc(db, this.collectionName, eventId);
      const snapshot = await getDoc(eventRef);

      if (!snapshot.exists()) {
        throw new Error(`L'événement ${eventId} n'existe pas`);
      }

      // Vérifier que l'utilisateur est bien le propriétaire de l'événement
      const eventData = snapshot.data();
      const currentUser = await userService.getCurrentUser();

      if (!currentUser || eventData.userId !== currentUser.uid) {
        throw new Error('Vous n\'êtes pas autorisé à modifier cet événement');
      }

      // Supprimer les propriétés qu'on ne devrait pas mettre à jour
      const { id, userId, createdAt, ...updateData } = updates;

      await updateDoc(eventRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });

      // Propager la mise à jour vers la collection spécifique si nécessaire
      // Cela assure la synchronisation entre la collection 'events' (Dashboard) et les collections spécifiques (Vue Invité)
      try {
        const specificCollectionName = this.getCollectionNameForType(eventData.type);
        if (specificCollectionName) {
          // Utiliser l'ID spécifique s'il existe (ex: weddingId), sinon utiliser l'ID de l'événement
          // Pour les événements récents, c'est le même ID. Pour les très anciens, ça peut différer.
          const specificId = eventData[`${eventData.type}Id`] || eventId;

          const specificRef = doc(db, specificCollectionName, specificId);
          const specificDoc = await getDoc(specificRef);

          if (specificDoc.exists()) {
            const specificUpdates: any = {};

            // Mapper les champs communs vers la structure spécifique
            // La plupart des collections spécifiques utilisent maintenant eventDetails (structure générique)
            const isGenericStructure = !!specificDoc.data()['eventDetails'];

            if (updates.name) {
              specificUpdates.name = updates.name;
            }

            if (updates.partners) {
              specificUpdates.partners = updates.partners;
            }

            if (updates.date !== undefined || updates.location !== undefined) {
              if (isGenericStructure) {
                // Structure générique (eventDetails)
                if (updates.date !== undefined) specificUpdates['eventDetails.date'] = updates.date ? (typeof updates.date === 'string' ? new Date(updates.date) : updates.date) : null;
                if (updates.location !== undefined) specificUpdates['eventDetails.location'] = typeof updates.location === 'string' ? { venue: updates.location } : updates.location;
              } else {
                // Structure spécifique (legacy weddingDetails, etc.)
                const detailField = `${eventData.type}Details`;
                if (updates.date !== undefined) specificUpdates[`${detailField}.date`] = updates.date ? (typeof updates.date === 'string' ? new Date(updates.date) : updates.date) : null;
                if (updates.location !== undefined) specificUpdates[`${detailField}.location`] = typeof updates.location === 'string' ? { venue: updates.location } : updates.location;
              }
            }

            if (Object.keys(specificUpdates).length > 0) {
              await updateDoc(specificRef, {
                ...specificUpdates,
                updatedAt: serverTimestamp()
              });
            }
          }
        }
      } catch (propagationError) {
        console.warn('⚠️ [EventsService] Erreur lors de la propagation de la mise à jour:', propagationError);
        // On ne bloque pas la mise à jour principale si la propagation échoue
      }

      // Tenter d'auto-compléter certaines tâches liées à la date ou au lieu
      try {
        await genericTasksService.autoCompleteTasks(eventId, eventData.type, updates);
        await tasksService.autoCompleteTasks(eventId, updates); // Pour les événements legacy (weddings)
      } catch (autoCompleteError) {
        console.warn('⚠️ [EventsService] Erreur lors de l\'auto-complétion des tâches:', autoCompleteError);
      }

      return true;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'événement ${eventId} :`, error);
      return false;
    }
  }

  /**
   * Supprime un événement
   */
  async deleteEvent(eventId: string): Promise<boolean> {
    try {
      const eventRef = doc(db, this.collectionName, eventId);
      const snapshot = await getDoc(eventRef);

      if (!snapshot.exists()) {
        throw new Error(`L'événement ${eventId} n'existe pas`);
      }

      // Vérifier que l'utilisateur est bien le propriétaire de l'événement
      const eventData = snapshot.data();
      const currentUser = await userService.getCurrentUser();

      if (!currentUser || eventData.userId !== currentUser.uid) {
        throw new Error('Vous n\'êtes pas autorisé à supprimer cet événement');
      }

      // Vérifier si c'est l'événement par défaut
      if (eventData.isDefault) {
        throw new Error('Vous ne pouvez pas supprimer l\'événement par défaut. Veuillez en définir un autre comme événement par défaut d\'abord.');
      }

      await deleteDoc(eventRef);
      return true;
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'événement ${eventId} :`, error);
      return false;
    }
  }

  /**
   * Définit un événement comme événement par défaut pour l'utilisateur
   */
  async setDefaultEvent(userId: string, eventId: string): Promise<boolean> {
    try {
      const events = await this.getUserEvents(userId);

      if (events.length === 0) {
        throw new Error('Aucun événement trouvé pour cet utilisateur');
      }

      // Vérifier que l'événement existe et appartient à l'utilisateur
      const eventToSet = events.find(event => event.id === eventId);
      if (!eventToSet) {
        throw new Error(`L'événement ${eventId} n'existe pas ou n'appartient pas à cet utilisateur`);
      }

      // Mettre à jour tous les événements pour retirer le statut par défaut
      const batch = [];
      for (const event of events) {
        if (event.isDefault && event.id !== eventId) {
          batch.push(this.updateEvent(event.id, { isDefault: false }));
        }
      }

      // Attendre que toutes les mises à jour soient terminées
      await Promise.all(batch);

      // Définir le nouvel événement par défaut
      await this.updateEvent(eventId, { isDefault: true });

      return true;
    } catch (error) {
      console.error(`Erreur lors de la définition de l'événement par défaut ${eventId} :`, error);
      return false;
    }
  }

  /**
   * Récupère l'événement par défaut de l'utilisateur
   */
  async getDefaultEvent(userId: string): Promise<Event | null> {
    try {
      const eventsQuery = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('isDefault', '==', true)
      );

      const snapshot = await getDocs(eventsQuery);

      if (snapshot.empty) {
        // Aucun événement par défaut trouvé, on récupère le premier événement de l'utilisateur
        const events = await this.getUserEvents(userId);

        if (events.length === 0) {
          return null;
        }

        // Définir le premier événement comme événement par défaut
        await this.setDefaultEvent(userId, events[0].id);
        return events[0];
      }

      const data = snapshot.docs[0].data();
      return {
        ...data,
        id: snapshot.docs[0].id,
        createdAt: data.createdAt?.toDate() || null,
        updatedAt: data.updatedAt?.toDate() || null
      } as Event;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'événement par défaut :`, error);
      return null;
    }
  }

  /**
   * Crée un événement par défaut pour un nouvel utilisateur
   */
  async createDefaultEvent(userId: string): Promise<Event | null> {
    try {
      const defaultEvent = {
        name: 'Mon mariage',
        type: 'wedding',
        isDefault: true
      };

      return await this.createEvent(userId, defaultEvent);
    } catch (error) {
      console.error('Erreur lors de la création de l\'événement par défaut :', error);
      return null;
    }
  }

  /**
   * Met à jour le budget d'un événement (unifié ou mariage traditionnel)
   */
  async updateEventBudget(eventId: string, totalBudget: number, estimatedGuestCount?: number) {
    try {
      if (estimatedGuestCount) {}

      // D'abord, essayer de récupérer l'événement depuis la collection events
      let eventRef = doc(db, 'events', eventId);
      let eventDoc = await getDoc(eventRef);

      // Si l'événement n'est pas trouvé dans events, essayer dans weddings
      if (!eventDoc.exists()) {
        eventRef = doc(db, 'weddings', eventId);
        eventDoc = await getDoc(eventRef);

        if (!eventDoc.exists()) {
          console.error('[DEBUG] Événement non trouvé lors de la mise à jour du budget');
          throw new Error('Événement non trouvé');
        }
      }

      const eventData = eventDoc.data();

      // Récupérer le budget actuel selon le type d'événement
      let currentBudget;
      let budgetFieldPath: string;

      if (eventData.eventDetails) {
        // C'est un mariage avec la nouvelle structure générique
        currentBudget = eventData.eventDetails?.budget || {};
        budgetFieldPath = 'eventDetails.budget';
      } else if (eventData.weddingDetails) {
        // C'est un mariage traditionnel (ancienne structure)
        currentBudget = eventData.weddingDetails?.budget || {};
        budgetFieldPath = 'weddingDetails.budget';
      } else {
        // C'est un événement unifié
        currentBudget = eventData.budget || {};
        budgetFieldPath = 'budget';
      }

      // Si le budget n'existe pas encore, créer une structure complète
      if (!currentBudget.categories || Object.keys(currentBudget.categories).length === 0) {
        // Créer le budget avec des catégories par défaut
        const defaultBudget = {
          total: totalBudget,
          categories: Object.fromEntries(
            Object.entries(DEFAULT_BUDGET_CATEGORIES).map(([name, percentage]) => [
              name,
              Math.round((totalBudget * percentage) / 100)
            ])
          ),
          estimatedGuestCount: estimatedGuestCount || 0
        };

        // Mettre à jour le document avec le budget complet
        await updateDoc(eventRef, {
          [budgetFieldPath]: defaultBudget,
          updatedAt: serverTimestamp()
        });

        return defaultBudget;
      } else {
        // Si le budget existe déjà, mettre à jour le total et le nombre d'invités estimé si fourni
        const updateData: Record<string, any> = {
          [`${budgetFieldPath}.total`]: totalBudget,
          updatedAt: serverTimestamp()
        };

        if (estimatedGuestCount !== undefined) {
          updateData[`${budgetFieldPath}.estimatedGuestCount`] = estimatedGuestCount;
        }

        await updateDoc(eventRef, updateData);

        // Retourner le budget mis à jour
        return {
          total: totalBudget,
          categories: currentBudget.categories,
          estimatedGuestCount: estimatedGuestCount !== undefined ? estimatedGuestCount : currentBudget.estimatedGuestCount
        };
      }
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la mise à jour du budget:', error);
      throw error;
    }
  }
}

export const eventsService = new EventsService();
export default eventsService; 